import { ComponentType, FC, ReactElement } from 'react';

import { UserPrivilegeName } from 'domain/UserProfile';
import { useAuthorization } from 'modules/authorization/hooks';

/**
 * Retires in HNS-357 with its last consumer, `AddButtonWithPrivilege`. Until then it keeps
 * `withNoCommunity` / `withCommunity`, which no consumer passes any more either - they went dead
 * when the sidebar stopped using this HOC (HNS-355) and are left alone so this slice does not touch
 * a file it is not otherwise changing the behaviour of.
 *
 * `forCompanyAdmin` and `forCompanyAndCommunityAdmin` are gone: neither ever had a consumer here.
 */
interface WithPrivilegeDependencyProps {
  privilegeAvailability?: UserPrivilegeName[];
  withNoCommunity?: boolean;
  withCommunity?: boolean;
}

export const withPrivilegeDependency =
  <P extends object>(WrappedComponent: ComponentType<P>): FC<P & WithPrivilegeDependencyProps> =>
  ({ privilegeAvailability = [], withNoCommunity, withCommunity, ...props }): ReactElement | null => {
    const { hasPrivilege, isWithoutCommunity } = useAuthorization();

    if (
      hasPrivilege(privilegeAvailability) ||
      (withNoCommunity && isWithoutCommunity) ||
      (withCommunity && !isWithoutCommunity)
    ) {
      return <WrappedComponent {...(props as P)} />;
    }

    return null;
  };
